import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Newspaper, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useInView } from '@/hooks/useInView';

interface NewsRow {
  id: string;
  slug: string;
  title_fr: string;
  title_en: string;
  excerpt_fr: string;
  excerpt_en: string;
  cover_image_url: string | null;
  category: string;
  published_at: string | null;
}

const LatestNewsBand = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState<NewsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const section = useInView();

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select(
          'id, slug, title_fr, title_en, excerpt_fr, excerpt_en, cover_image_url, category, published_at'
        )
        .eq('published', true)
        .order('published_at', { ascending: false })
        .limit(3);
      if (!error && data) setArticles(data as NewsRow[]);
      setLoading(false);
    };
    load();
  }, []);

  const fmtDate = (iso: string | null) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Hide section entirely if loaded but empty (avoid empty band on homepage)
  if (!loading && articles.length === 0) return null;

  return (
    <section
      ref={section.ref}
      aria-labelledby="latest-news-title"
      className="py-24 bg-background relative"
    >
      <div className="container mx-auto px-4">
        <div
          className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 ${
            section.isInView ? 'opacity-100 animate-fade-in' : 'opacity-0'
          }`}
        >
          <div>
            <span className="inline-flex items-center gap-2 text-accent font-semibold text-sm uppercase tracking-wider mb-3">
              <Newspaper className="w-4 h-4" aria-hidden="true" />
              {language === 'fr' ? 'Dernières actualités' : 'Latest news'}
            </span>
            <h2
              id="latest-news-title"
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground"
            >
              {t('news.title')}
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl text-lg">{t('news.intro')}</p>
          </div>
          <Link to="/news" className="hidden md:inline-flex">
            <Button
              variant="outline"
              className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground font-semibold group"
            >
              {language === 'fr' ? 'Toutes les actualités' : 'All news'}
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-16" aria-live="polite">
            <Loader2 className="w-8 h-8 animate-spin text-primary" aria-hidden="true" />
            <span className="sr-only">{language === 'fr' ? 'Chargement…' : 'Loading…'}</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article, index) => {
              const title = language === 'fr' ? article.title_fr : article.title_en;
              const excerpt = language === 'fr' ? article.excerpt_fr : article.excerpt_en;
              return (
                <article
                  key={article.id}
                  className={`group bg-card rounded-xl border border-border overflow-hidden hover-lift flex flex-col ${
                    section.isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${(index + 1) * 100}ms` }}
                >
                  <Link
                    to={`/news/${article.slug}`}
                    className="block aspect-[16/9] overflow-hidden bg-secondary"
                    aria-label={title}
                  >
                    {article.cover_image_url ? (
                      <img
                        src={article.cover_image_url}
                        alt={title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                        <Newspaper className="w-12 h-12 text-primary/40" aria-hidden="true" />
                      </div>
                    )}
                  </Link>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
                        {fmtDate(article.published_at)}
                      </span>
                      {article.category && (
                        <span className="inline-block bg-accent/10 text-accent px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide">
                          {article.category}
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-lg text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      <Link to={`/news/${article.slug}`}>{title}</Link>
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">
                      {excerpt}
                    </p>
                    <Link
                      to={`/news/${article.slug}`}
                      className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors group/link"
                    >
                      {t('news.read')}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-10 flex md:hidden justify-center">
          <Link to="/news">
            <Button
              variant="outline"
              className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground font-semibold group"
            >
              {language === 'fr' ? 'Toutes les actualités' : 'All news'}
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestNewsBand;
